// AnnouncementModal.tsx - Elegant pop-up modal for announcements
import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X, ArrowRight } from '@phosphor-icons/react';
import { Transition } from '@headlessui/react';
import { sanitizeHtml } from '../utils/sanitizeHtml';

const ANNOUNCEMENT_BODY_CONFIG = {
  ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'b', 'i', 'u', 'a', 'ul', 'ol', 'li', 'h2', 'h3', 'h4', 'span'],
  ALLOWED_ATTR: ['href', 'target', 'rel', 'class'],
};

interface AnnouncementData {
  id: string;
  title: string;
  body: string;
  imageUrl?: string | null;
  ctaText?: string | null;
  ctaLink?: string | null;
  ctaColor?: 'blue' | 'green' | 'orange' | 'purple';
}

interface AnnouncementModalProps {
  announcement: AnnouncementData;
  onDismiss: () => void;
}

const AnnouncementModal: React.FC<AnnouncementModalProps> = ({
  announcement,
  onDismiss,
}) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const [isShowing, setIsShowing] = React.useState(false);

  // Trigger animation after mount
  useEffect(() => {
    const timer = setTimeout(() => setIsShowing(true), 10);
    return () => clearTimeout(timer);
  }, []);

  // Focus trap and escape key handling
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsShowing(false);
        setTimeout(onDismiss, 200);
      }
    };

    document.addEventListener('keydown', handleEscape);
    document.body.style.overflow = 'hidden';
    modalRef.current?.focus();

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [onDismiss]);

  const handleDismiss = () => {
    setIsShowing(false);
    setTimeout(onDismiss, 200);
  };

  const handleCtaClick = () => {
    handleDismiss();
  };

  // CTA button color variants
  const ctaColors: Record<string, string> = {
    blue: 'bg-blue hover:bg-blue-dark shadow-[0_4px_14px_rgba(59,130,246,0.4)]',
    green: 'bg-green hover:bg-green-dark shadow-[0_4px_14px_rgba(34,197,94,0.4)]',
    orange: 'bg-orange hover:bg-orange-dark shadow-[0_4px_14px_rgba(249,115,22,0.4)]',
    purple: 'bg-purple hover:bg-purple-dark shadow-[0_4px_14px_rgba(139,92,246,0.4)]',
  };
  const ctaColor = ctaColors[announcement.ctaColor || 'blue'] || ctaColors.blue;

  const modalContent = (
    <Transition
      appear
      show={isShowing}
      as={React.Fragment}
    >
      <div
        className="fixed inset-0 z-[9999] overflow-y-auto"
        role="dialog"
        aria-modal="true"
        aria-labelledby="announcement-title"
      >
        {/* Backdrop with blur */}
        <Transition.Child
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div
            className="fixed inset-0 bg-grey-darker/60 backdrop-blur-sm"
            onClick={handleDismiss}
            aria-hidden="true"
          />
        </Transition.Child>

        {/* Modal container */}
        <div className="flex min-h-screen items-center justify-center p-4">
          <Transition.Child
            enter="ease-out duration-300"
            enterFrom="opacity-0 scale-95 translate-y-4"
            enterTo="opacity-100 scale-100 translate-y-0"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 scale-100 translate-y-0"
            leaveTo="opacity-0 scale-95 translate-y-4"
          >
            <div
              ref={modalRef}
              tabIndex={-1}
              className="
                relative w-full max-w-lg
                bg-white dark:bg-grey-darker
                rounded-2xl
                shadow-2xl dark:shadow-[0_25px_50px_-12px_rgba(0,0,0,0.5)]
                overflow-hidden
                outline-none
                transform transition-all
              "
              onClick={(e) => e.stopPropagation()}
            >
              {/* Close button - elegant floating style */}
              <button
                onClick={handleDismiss}
                className="
                  absolute top-4 right-4 z-10
                  w-8 h-8 flex items-center justify-center
                  rounded-full
                  bg-white/90 dark:bg-grey/90
                  backdrop-blur-sm
                  text-grey hover:text-grey-darker
                  dark:text-grey-light dark:hover:text-white
                  shadow-md hover:shadow-lg
                  transition-all duration-200
                  hover:scale-105
                "
                aria-label="Close announcement"
              >
                <X weight="bold" className="w-4 h-4" />
              </button>

              {/* Optional header image with gradient overlay */}
              {announcement.imageUrl && (
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={announcement.imageUrl}
                    alt=""
                    className="w-full h-full object-cover"
                  />
                  {/* Gradient overlay for text readability */}
                  <div className="
                    absolute inset-0
                    bg-gradient-to-t from-white/80 via-transparent to-transparent
                    dark:from-grey-darker/90 dark:via-transparent dark:to-transparent
                  " />
                </div>
              )}

              {/* Content area */}
              <div className={`px-8 ${announcement.imageUrl ? 'pt-6' : 'pt-8'} pb-6`}>
                {/* Title */}
                <h2
                  id="announcement-title"
                  className="
                    text-2xl font-bold
                    text-grey-darker dark:text-white
                    leading-tight mb-4
                  "
                >
                  {announcement.title}
                </h2>

                {/* Body - supports rich HTML */}
                <div
                  className="
                    text-grey dark:text-grey-light
                    leading-relaxed
                    prose prose-sm dark:prose-invert
                    max-w-none
                    [&>p]:mb-3 [&>p:last-child]:mb-0
                    [&>ul]:list-disc [&>ul]:pl-5 [&>ul]:mb-3
                    [&>ol]:list-decimal [&>ol]:pl-5 [&>ol]:mb-3
                    [&_a]:text-blue [&_a]:underline [&_a:hover]:text-blue-dark
                  "
                  dangerouslySetInnerHTML={{ __html: sanitizeHtml(announcement.body, ANNOUNCEMENT_BODY_CONFIG) }}
                />
              </div>

              {/* Footer with CTA */}
              <div className="px-8 pb-8 pt-2">
                {announcement.ctaText && announcement.ctaLink ? (
                  <a
                    href={announcement.ctaLink}
                    target={announcement.ctaLink.startsWith('http') ? '_blank' : undefined}
                    rel={announcement.ctaLink.startsWith('http') ? 'noopener noreferrer' : undefined}
                    onClick={handleCtaClick}
                    className={`
                      inline-flex items-center justify-center gap-2
                      w-full px-6 py-3
                      text-white font-medium
                      rounded-xl
                      ${ctaColor}
                      transition-all duration-200
                      hover:translate-y-[-1px]
                      focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue
                      dark:focus:ring-offset-grey-darker
                    `}
                  >
                    {announcement.ctaText}
                    <ArrowRight weight="bold" className="w-4 h-4" />
                  </a>
                ) : (
                  <button
                    onClick={handleDismiss}
                    className="
                      w-full px-6 py-3
                      text-grey-darker dark:text-grey-light
                      font-medium
                      rounded-xl
                      border border-grey-very-light dark:border-grey
                      hover:bg-grey-extra-light dark:hover:bg-grey
                      transition-colors duration-200
                    "
                  >
                    Got it
                  </button>
                )}
              </div>

              {/* Subtle bottom accent line */}
              <div className="h-1 bg-gradient-to-r from-blue via-purple to-orange" />
            </div>
          </Transition.Child>
        </div>
      </div>
    </Transition>
  );

  return createPortal(modalContent, document.body);
};

export default AnnouncementModal;
